import React, { useEffect, useState } from 'react';

interface AdSenseProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  adStyle?: React.CSSProperties;
  className?: string;
  responsive?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

// Global tracking to prevent duplicate ad pushes
const pushedAdSlots = new Set<string>();

// Temporary flag to disable AdSense if needed
const DISABLE_ADSENSE = false;

const AdSense: React.FC<AdSenseProps> = ({
  adSlot,
  adFormat = 'auto',
  adStyle = { display: 'block' },
  className = '',
  responsive = true
}) => {
  const [isAdVisible, setIsAdVisible] = useState(true);
  const [adLoaded, setAdLoaded] = useState(false);

  // If AdSense is disabled, don't render anything
  if (DISABLE_ADSENSE) {
    return null;
  }

  useEffect(() => {
    const checkAdVisibility = () => {
      // Check if ad element exists and has content
      const adElement = document.querySelector(`[data-ad-slot="${adSlot}"]`);
      if (adElement) {
        const hasContent = adElement.children.length > 0 || adElement.innerHTML.trim() !== '';
        const hasHeight = adElement.offsetHeight > 0;
        
        // If ad has no content or no height after 3 seconds, hide it
        setTimeout(() => {
          if (!hasContent || !hasHeight) {
            setIsAdVisible(false);
          } else {
            setAdLoaded(true);
          }
        }, 3000);
      } else {
        // If ad element doesn't exist, hide the container
        setTimeout(() => {
          setIsAdVisible(false);
        }, 5000);
      }
    };

    try {
      if (typeof window !== 'undefined' && window.adsbygoogle) {
        // Only push if this slot hasn't been pushed before
        if (!pushedAdSlots.has(adSlot)) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            pushedAdSlots.add(adSlot);
            console.log(`AdSense pushed for slot: ${adSlot}`);
            
            // Check ad visibility after a delay
            setTimeout(checkAdVisibility, 2000);
          } catch (pushError) {
            console.warn(`Failed to push AdSense for slot ${adSlot}:`, pushError);
            setIsAdVisible(false);
          }
        } else {
          // Ad already pushed, just check visibility
          setTimeout(checkAdVisibility, 1000);
        }
      } else {
        // If AdSense is not available, hide the ad
        setIsAdVisible(false);
      }
    } catch (error) {
      console.error('AdSense error:', error);
      setIsAdVisible(false);
    }

    // Also listen for AdSense events
    const handleAdLoad = () => {
      setAdLoaded(true);
    };

    const handleAdError = () => {
      setIsAdVisible(false);
    };

    // Listen for AdSense events
    document.addEventListener('adsbygoogle:loaded', handleAdLoad);
    document.addEventListener('adsbygoogle:error', handleAdError);

    return () => {
      document.removeEventListener('adsbygoogle:loaded', handleAdLoad);
      document.removeEventListener('adsbygoogle:error', handleAdError);
    };
  }, [adSlot]);

  // Don't render anything if ad should not be visible
  if (!isAdVisible) {
    return null;
  }

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={adStyle}
      data-ad-client="ca-pub-9182398897372674"
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-full-width-responsive={responsive ? 'true' : 'false'}
    />
  );
};

export default AdSense;
